import type { BuildFromSelectionsScheduleDay } from '@travel-planner/shared/schemas';
import type {
  GeneratorFailedEntry,
  GeneratorScheduleEntry,
} from './use-agent-panel-store';
import { isTRPCClientError } from '@trpc/client';
import { useCallback } from 'react';
import { trpc } from '@/shared/api/trpc';
import { useAgentPanelStore } from './use-agent-panel-store';

// Flatten the day-grouped schedule (`BuildFromSelectionsOutput['schedule']`)
// into one entry per slot so the commit pipeline can iterate without nested
// loops and the partial-failure list can label each row.
export const flattenSchedule = (
  schedule: ReadonlyArray<BuildFromSelectionsScheduleDay>
): ReadonlyArray<GeneratorScheduleEntry> =>
  schedule.flatMap((day) =>
    day.slots.map((slot) => ({
      dayIndex: day.dayIndex,
      slot: slot.slot,
      startTime: slot.startTime,
      productId: slot.productId,
    }))
  );

const FALLBACK_REASON = 'Could not add this item.';

const extractMessage = (err: unknown): string => {
  if (err instanceof Error && err.message) return err.message;
  return FALLBACK_REASON;
};

// `isTRPCClientError` resolves to `TRPCClientError<InferrableClientTypes>`
// where `data` widens to `any`. Narrow it ourselves to keep the badge code
// extraction type-safe.
type TRPCErrorData = { httpStatus?: number; code?: string };

// Surface the HTTP status so the partial-failure badge shows "412" / "404".
// Falls back to the TRPC error code (e.g. "PRECONDITION_FAILED") when
// httpStatus isn't present.
const extractCode = (err: unknown): string | null => {
  if (isTRPCClientError(err)) {
    const data = err.data as TRPCErrorData | undefined;
    if (data?.httpStatus != null) return String(data.httpStatus);
    if (data?.code) return data.code;
  }
  return null;
};

/**
 * Drives the "Add to itinerary" pipeline through the generator's three
 * terminal phases (`committing` → `done` | `partial-failure`).
 *
 * SEQUENTIAL by design — the DB trigger
 * `check_category_subcategory_validity` on `itinerary_item.content_category`
 * does not tolerate concurrent writes hitting the same itinerary version
 * (see `apps/itineraries/api/src/itinerary-items/service.ts:78`). Don't
 * switch to `Promise.all` / parallel issue.
 *
 * After every entry the store is updated so the spinner reflects activity.
 * After the loop:
 *   - Any failures → `partial-failure` (the panel surfaces "Retry remaining"
 *     which calls this same hook again with `failed.map(f => f.entry)`).
 *   - All succeeded → invalidate every tRPC query so the planner reflects
 *     the new items, then `done`.
 */
export function useGeneratorCommit() {
  const utils = trpc.useUtils();
  // `useMutation()` returns a fresh object every render. Pull the stable
  // function reference out so `commit` keeps a stable identity across
  // renders and downstream `useCallback`s in PreviewStep / BudgetStep
  // aren't invalidated on every parent re-render.
  const { mutateAsync: createItem } = trpc.itineraryItems.create.useMutation();
  const setGeneratorState = useAgentPanelStore((s) => s.setGeneratorState);

  return useCallback(
    async (
      entries: ReadonlyArray<GeneratorScheduleEntry>,
      itineraryVersionId: string
    ): Promise<void> => {
      const total = entries.length;
      setGeneratorState({
        phase: 'committing',
        total,
        succeeded: 0,
        currentDayIndex: null,
        failed: [],
      });

      const failed: GeneratorFailedEntry[] = [];
      let succeeded = 0;
      let currentDayIndex: number | null = null;

      for (const entry of entries) {
        // Advance the label as we *start* committing this entry, not after
        // it lands — `CommittingStep` shows "currently processing day N",
        // including for entries that ultimately fail.
        currentDayIndex = entry.dayIndex;
        try {
          await createItem({
            itineraryVersionId,
            dayIndex: entry.dayIndex,
            startTime: entry.startTime,
            productId: entry.productId,
          });
          succeeded += 1;
        } catch (err) {
          failed.push({
            entry,
            code: extractCode(err),
            reason: extractMessage(err),
          });
        }
        setGeneratorState({
          phase: 'committing',
          total,
          succeeded,
          currentDayIndex,
          failed: [...failed],
        });
      }

      if (failed.length > 0) {
        setGeneratorState({
          phase: 'partial-failure',
          total,
          succeeded,
          failed,
          versionId: itineraryVersionId,
        });
        return;
      }

      // Future-proof: the planner will start reading itinerary data once the
      // dedicated routes land. Invalidate the whole tRPC tree so any cached
      // itinerary queries (none today) refetch on next mount.
      await utils.invalidate();
      setGeneratorState({ phase: 'done', total });
    },
    [createItem, utils, setGeneratorState]
  );
}
